import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Webcam from 'react-webcam';
import * as faceapi from 'face-api.js';
import Button from 'components/shared/Button';
import styles from './style.module.css';
import BackAndNextButton from 'layouts/RegistrationLayoutNew/Buttons/BackAndNextButton';
import BackButton from 'layouts/RegistrationLayoutNew/Buttons/BackButton';

const TakePhotoSection = ({
  handleConvertPhotoIntoBinary,
  userExist,
  uploadPhoto,
  userExistenceData,
  isMobile,
  photo,
  takePhoto,
  onNext,
  onBack,
  editDetailsOnNext,
  handleEditDetailsOnNext,
  updateOlsTracking,
  trackingId,
  emailAddress,
  title,
  referralPerson,
  setError,
}) => {
  const [permission, setPermission] = useState('loading');
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [isFaceDetected, setIsFaceDetected] = useState(false);
  const [detectionMessage, setDetectionMessage] = useState('');
  const [webcamReady, setWebcamReady] = useState(false);

  const webcamRef = useRef(null);
  const detectionIntervalRef = useRef(null);

  // Initialize camera and check permissions
  useEffect(() => {
    window.scrollTo(0, 0);
    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then(() => setPermission('granted'))
      .catch(error => {
        if (error.name === 'NotAllowedError') {
          setPermission('denied');
        } else if (error.name === 'NotReadableError' || error.name === 'AbortError') {
          setPermission('occupied');
        } else {
          setPermission('denied');
        }
      });
  }, []);

  // Load face-api.js models
  useEffect(() => {
    const loadModels = async () => {
      try {
        setDetectionMessage('Loading face detection models...');
        // Set the path to the models directory
        const MODEL_URL = `${window.location.origin}/models`;

        // Load required models
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        ]);

        setModelsLoaded(true);
        setDetectionMessage('Face detection ready');
        console.warn('Face detection models loaded successfully');
      } catch (error) {
        console.error('Error loading face detection models:', error);
        setDetectionMessage('Face detection unavailable - proceed anyway');
        // Allow users to proceed even if models fail to load
        setModelsLoaded(false);
        setError('Face detection is unavailable. Please refresh the page and try again.');
      }
    };

    if (permission === 'granted') {
      loadModels();
    }
  }, [permission]);

  // Start live face detection when webcam and models are ready
  useEffect(() => {
    if (webcamReady && modelsLoaded && !photo) {
      // Clear any existing interval
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current);
      }

      setDetectionMessage('Starting face detection...');

      // Start detection loop
      detectionIntervalRef.current = setInterval(async () => {
        if (
          webcamRef.current &&
          webcamRef.current.video &&
          webcamRef.current.video.readyState === 4
        ) {
          try {
            const detections = await faceapi
              .detectAllFaces(
                webcamRef.current.video,
                new faceapi.TinyFaceDetectorOptions({ scoreThreshold: 0.3 }) // Lower threshold to detect all faces
              )
              .withFaceLandmarks();

            // First check if there are multiple faces with ANY confidence level
            if (detections && detections.length > 1) {
              setIsFaceDetected(false);
              setDetectionMessage(
                `${detections.length} faces detected - Only one face allowed`
              );
            } else if (detections && detections.length === 1) {
              const face = detections[0];
              const score = face.detection.score;

              // Check if the single face has high enough confidence
              if (score >= 0.7) {
                setIsFaceDetected(true);
                setDetectionMessage(
                  `Face detected (${score.toFixed(2)}) - Ready to capture`
                );
              } else {
                setIsFaceDetected(false);
                setDetectionMessage(
                  `Low confidence (${score.toFixed(2)}) - Please face the camera directly`
                );
              }
            } else {
              setIsFaceDetected(false);
              setDetectionMessage(
                'No face detected - Please position your face in the frame'
              );
            }
          } catch (error) {
            console.error('Face detection error:', error);
            setDetectionMessage(`Detection error: ${error.message}`);
            setIsFaceDetected(false);
          }
        }
      }, 500); // Check frequently for better responsiveness

      return () => {
        if (detectionIntervalRef.current) {
          clearInterval(detectionIntervalRef.current);
        }
      };
    }
  }, [webcamReady, modelsLoaded, photo]);

  const isShutterClosed = dataUri => {
    const base64Header = 'data:image/jpeg;base64,/9j/';
    return dataUri.startsWith(base64Header) && dataUri.length < 5000;
  };

  const handleTakePhoto = () => {
    if (!webcamRef.current) return;

    try {
      const dataUri = webcamRef.current.getScreenshot();

      if (dataUri && dataUri !== 'data:,' && !isShutterClosed(dataUri)) {
        setIsFaceDetected(false); // Reset validation
        takePhoto(dataUri);

        // Stop detection interval
        if (detectionIntervalRef.current) {
          clearInterval(detectionIntervalRef.current);
        }
      } else {
        if (isShutterClosed(dataUri)) {
          alert('Camera shutter might be closed. Please open it and try again.');
        } else if (dataUri === 'data:,') {
          alert('Camera is not ready yet. Please wait a moment...');
        } else {
          setDetectionMessage('Camera is not ready yet. Please wait a moment...');
        }
      }
    } catch (error) {
      console.error('Error capturing photo:', error);
      setDetectionMessage(`Capture error: ${error.message}`);
    }
  };

  const handleNextButton = () => {
    if (!userExist) {
      if (editDetailsOnNext === 2) {
        handleEditDetailsOnNext();
      } else {
        onNext();
      }
    } else if (userExist) {
      const blob = handleConvertPhotoIntoBinary(photo);
      const formData = new FormData();
      formData.append('profilePicture', blob, 'photo.jpg');
      uploadPhoto(userExistenceData, formData);
      if (editDetailsOnNext === 2) {
        handleEditDetailsOnNext();
      } else {
        onNext();
      }
    }
    handleTrackingApi();
  };

  const handleTrackingApi = () => {
    const data = {
      session: trackingId,
      email: emailAddress,
      currentPage: title,
      data: null,
      isFormerMember: userExist,
      isReferral: Object.keys(referralPerson).length !== 0,
      referrerName: Object.keys(referralPerson).length !== 0 ? referralPerson.name : null,
      referrerCaid:
        Object.keys(referralPerson).length !== 0 ? referralPerson?.caID : null,
    };
    updateOlsTracking(data);
  };

  // Permission status
  if (permission === 'loading') {
    return <div className={styles.permissionText}>Checking camera permissions...</div>;
  }

  if (permission === 'denied') {
    return (
      <div className={styles.permissionText}>
        Camera access was denied. Please enable camera permissions and refresh the page.
      </div>
    );
  }

  if (permission === 'occupied') {
    return (
      <div className={styles.permissionText}>
        Camera is currently in use by another application. Please close other applications
        using the camera and refresh the page.
      </div>
    );
  }

  return (
    <div className={styles.body}>
      <div className={`${styles.container} ${isMobile ? styles.mobile : ''}`}>
        {photo ? (
          <div className={styles.imgSection}>
            <img src={photo} className={styles.img} alt="photos" />
          </div>
        ) : (
          <div className={styles.cameraContainer}>
            <Webcam
              ref={webcamRef}
              audio={false}
              screenshotFormat="image/jpeg"
              videoConstraints={{
                width: 350,
                height: 350,
                facingMode: 'user',
              }}
              mirrored={true}
              className={styles.videoFeed}
              onUserMedia={() => {
                console.warn('Webcam access granted');
                setDetectionMessage('Webcam initialized');
                setWebcamReady(true);
              }}
              onUserMediaError={err => {
                console.error('Webcam error:', err);
                setDetectionMessage(`Webcam error: ${err.name}`);
              }}
            />

            {/* Status messages */}
            {/* <div className={styles.detectionInfo}>{detectionMessage}</div> */}

            {/* Capture button overlay */}
            <div className={styles.captureButtonOverlay}>
              <Button
                disabled={!webcamReady || !modelsLoaded || !isFaceDetected}
                className={styles.captureButton}
                onClick={handleTakePhoto}
              >
                {!webcamReady || !modelsLoaded ? 'Loading...' : 'Capture'}
              </Button>
            </div>
          </div>
        )}

        <div className={`${styles.buttonsContainer} ${isMobile ? styles.mobile : ''}`}>
          <Button
            disabled={!photo}
            className={`${styles.retakeButton} ${isMobile ? styles.mobile : ''}`}
            onClick={() => {
              takePhoto('');
              setIsFaceDetected(false);
            }}
          >
            Retake
          </Button>
          <Button
            disabled={!photo}
            className={`${styles.nextButton} ${isMobile ? styles.mobile : ''}`}
            onClick={handleNextButton}
          >
            Next
          </Button>
        </div>

        {isMobile ? (
          <BackAndNextButton
            backButtonName="Back"
            nextButtonName="Next"
            nextButtonDisable={!photo}
            onBack={onBack}
            onNext={handleNextButton}
          />
        ) : (
          <BackButton onBack={onBack} backButtonName="Back" />
        )}
      </div>
    </div>
  );
};

TakePhotoSection.propTypes = {
  photo: PropTypes.string.isRequired,
  takePhoto: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
};

export default TakePhotoSection;